import { Collapse, Image, Typography } from 'antd';
import { Investment, InvestmentKey, Investor } from 'procura-shared';
import React from 'react';
import { useTranslation } from 'react-i18next';
import calls from '../api/calls';
import '../styles/investment-info.less';
import { PLACEHOLDER_IMG } from '../utilities/Constants';
import { DeleteButton, DetailsTable, InvestmentDocuments, SharesTable } from './Components';
import { UpdateImageButton } from './UpdateImageButton';

type InvestmentInfoProps = {
  investment: Investment;
  investors: Investor[]; // All investors in the investments
  loading?: boolean;
  adminMode?: boolean;
  onInvestorSelect?: (investor: Investor) => void;
};

const TABLES: Record<string, InvestmentKey[]> = {
  property: ['address', 'completionDate', 'purchasePrice', 'exchangeRateAtPurchase'],
  lease: ['tenant', 'garantor', 'leaseStart', 'leaseEnd', 'rentalIncome', 'rentReview', 'nextRentReview'],
  lenders: ['firstPriorityBank', 'loanExpiry', 'interestRate', 'secondPriorityBank'],
  collaborators: ['propertyManagement', 'dkAuditor', 'ukAuditor', 'boardLiabilityInsurance'],
};

export const InvestmentInfo = ({
  investment,
  investors = [],
  loading = false,
  adminMode = false,
  onInvestorSelect,
}: InvestmentInfoProps) => {
  const { t } = useTranslation();

  const investorSelectHandler = (investor: Investor) => onInvestorSelect?.(investor);

  const shareChangeHandler = async (investorId: string, newValue: number) => {
    await calls.investments.updateShare(investment.id, investorId, newValue);
  };

  const roleChangeHandler = async (investorId: string, newValue: string | null) => {
    await calls.investments.updateRole(investment.id, investorId, newValue);
  };

  return (
    <div className="pg-investment-info">
      <Collapse defaultActiveKey={['0', '1', '2', '3', '4']}>
        <Collapse.Panel header={t('image')} key="0">
          <Image className="pg-investment-image " src={investment?.imageURL} fallback={PLACEHOLDER_IMG} />
          {!!adminMode && <UpdateImageButton investmentId={investment?.id} style={{ marginTop: 12 }} />}
        </Collapse.Panel>
        <Collapse.Panel header={t('information')} key="1">
          {Object.keys(TABLES).map((key) => (
            <React.Fragment key={key}>
              <Typography.Title className="pg-detail-table-title" level={4}>
                {t(key)}
              </Typography.Title>
              <DetailsTable
                adminMode={adminMode}
                details={investment}
                loading={loading}
                // keys={adminMode ? constants.INVESTMENT_KEYS : constants.INVESTMENT_MIN_KEYS}
                keys={TABLES[key]}
              />
            </React.Fragment>
          ))}
        </Collapse.Panel>
        {/* {!adminMode && (
          <Collapse.Panel header={t('timeline')} key="2">
            <InvestmentTimeline investment={investment} loading={loading} adminMode={adminMode}></InvestmentTimeline>
          </Collapse.Panel>
        )} */}
        <Collapse.Panel header={t('documents')} key="3">
          <InvestmentDocuments investment={investment} loading={loading} adminMode={adminMode} />
        </Collapse.Panel>
        <Collapse.Panel header={t('investors')} key="4">
          <SharesTable
            shares={investment?.shares}
            roles={investment?.roles}
            investors={investors}
            loading={loading}
            adminMode={adminMode}
            onSelect={investorSelectHandler}
            onChange={shareChangeHandler}
            onRoleChange={roleChangeHandler}
          />
        </Collapse.Panel>
      </Collapse>
      {!!adminMode && <DeleteButton type="Investment" investment={investment} style={{ marginTop: 12 }} />}
    </div>
  );
};
